import api from '@/lib/api';
import type { AccessType, CreateAccessTypeInput, UpdateAccessTypeInput } from '@hygge/shared';
import { CACHE_TIME } from '@hygge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const accessTypesKeys = {
  all: ['access-types'] as const,
  lists: () => [...accessTypesKeys.all, 'list'] as const,
  list: () => [...accessTypesKeys.lists()] as const,
};

async function getAccessTypes(includeInactive = false): Promise<AccessType[]> {
  const params = includeInactive ? { includeInactive: 'true' } : {};
  const { data } = await api.get<AccessType[]>('/access-types', { params });
  return data;
}

async function createAccessType(input: CreateAccessTypeInput): Promise<AccessType> {
  const { data } = await api.post<AccessType>('/access-types', input);
  return data;
}

async function updateAccessType(id: string, input: UpdateAccessTypeInput): Promise<AccessType> {
  const { data } = await api.put<AccessType>(`/access-types/${id}`, input);
  return data;
}

async function deleteAccessType(id: string): Promise<void> {
  await api.delete(`/access-types/${id}`);
}

async function toggleAccessTypeActive(id: string): Promise<AccessType> {
  const { data } = await api.patch<AccessType>(`/access-types/${id}/toggle-active`);
  return data;
}

// `enabled` lets a caller skip the read when it holds none of the codes the
// catalog reads for (any one of the module's four, since HW-942) — the request
// could only 403.
export function useAccessTypes(includeInactive = false, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...accessTypesKeys.list(), { includeInactive }],
    queryFn: () => getAccessTypes(includeInactive),
    enabled: options?.enabled ?? true,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useCreateAccessType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAccessType,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessTypesKeys.all });
    },
  });
}

export function useUpdateAccessType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateAccessTypeInput & { id: string }) =>
      updateAccessType(id, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessTypesKeys.all });
    },
  });
}

export function useDeleteAccessType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAccessType,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessTypesKeys.all });
      queryClient.refetchQueries({ queryKey: accessTypesKeys.all });
    },
  });
}

export function useToggleAccessTypeActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: toggleAccessTypeActive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessTypesKeys.all });
    },
  });
}
